import {
  Box,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
  HStack,
  useColorModeValue
} from '@chakra-ui/react';
import { useLyricsTheme } from '../context/LyricsThemeContext';
import React from 'react';

interface ProgressBarProps {
  progressMs: number;
  duration: number;
  setProgressMs: (time: number) => void;
  startProgressTimer: (startTime: number, isPlaying: boolean) => void;
  playbackState: any;
  accentColor?: string;
  isFullWidth?: boolean;
}

const ProgressBar = ({
  progressMs,
  duration,
  setProgressMs,
  startProgressTimer,
  playbackState,
  accentColor = 'blue',
  isFullWidth = false
}: ProgressBarProps) => {
  // Get dominant color from context
  const { dominantColor } = useLyricsTheme();
  
  // Format time for display
  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Handle slider change (seeking)
  const handleSliderChange = (value: number) => {
    if (isFullWidth) {
      // In Full Width, the value is already a percentage
      const seekPositionMs = Math.floor((value / 100) * duration);
      setProgressMs(seekPositionMs);
      
      // If the song is playing, restart the timer from the new position
      if (playbackState?.is_playing) {
        startProgressTimer(seekPositionMs, true);
      }
    } else {
      // In standard mode, the value is already in milliseconds
      setProgressMs(value);
      
      // If the song is playing, restart the timer from the new position
      if (playbackState?.is_playing) {
        startProgressTimer(value, true);
      }
    }
  };

  
  // UI colors for controls
  const controlMutedTextColor = useColorModeValue('gray.500', 'gray.400');
  
  // Use dominant color if available, otherwise fall back to accent color
  const sliderColor = dominantColor || accentColor;
  
  // Set the dominant color as a CSS variable if available
  React.useEffect(() => {
    if (dominantColor) {
      document.documentElement.style.setProperty('--dominant-color', dominantColor);
    }
  }, [dominantColor]);
  
  // Custom styles to override Chakra UI's default color scheme
  const sliderStyles = {
    '.chakra-slider__filled-track': {
      backgroundColor: 'var(--dominant-color, var(--color-primary)) !important'
    },
    '.chakra-slider__thumb': {
      backgroundColor: 'var(--dominant-color, var(--color-primary)) !important',
      borderColor: 'var(--color-border) !important'
    },
    '.chakra-slider__track': {
      backgroundColor: 'var(--color-control-muted) !important'
    }
  };
  
  if (isFullWidth) {
    return (
      <HStack spacing={1} width="100%" pb={3}>
        <Text 
          fontSize="xs" 
          width="40px" 
          textAlign="right"
          className="time-display"
        >
          {formatTime(progressMs)}
        </Text>
        
        <Slider
          aria-label="progress-bar"
          value={progressMs}
          min={0}
          max={duration}
          onChange={handleSliderChange}
          focusThumbOnChange={false}
          sx={sliderStyles}
        >
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb />
        </Slider>
        
        <Text 
          fontSize="xs" 
          color="var(--tesla-text-secondary)"
          width="40px"
          className="time-display"
        >
          {formatTime(duration)}
        </Text>
      </HStack>
    );
  }
  
  // Standard progress bar
  return (
    <Box width="100%">
      <Box mb={2}>
        <Slider
          aria-label="progress-bar"
          value={progressMs}
          min={0}
          max={duration}
          onChange={handleSliderChange}
          focusThumbOnChange={false}
          sx={sliderStyles}
        >
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb />
        </Slider>
      </Box>
      
      <HStack justify="space-between">
        <Text fontSize="sm" color={controlMutedTextColor}>
          {formatTime(progressMs)}
        </Text>
        
        <Text fontSize="sm" color={controlMutedTextColor}>
          {formatTime(duration)}
        </Text>
      </HStack>
    </Box>
  );
};

export default ProgressBar; 